import { useRef, useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import SafeImg from './SafeImg';
import BrokenMedia from './BrokenMedia';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import thumbnail_video_blur from '@/assets/imgs/placeholders/thumbnail_video_blur.jpg';
import { getStreamEmbedUrl } from '@/shared/utils/streamUrl';
import { escapeHtml } from '@/shared/utils/escapeHtml';
import lightGallery from 'lightgallery';
import lgThumbnail from 'lightgallery/plugins/thumbnail';
import lgFullscreen from 'lightgallery/plugins/fullscreen';

const VideoGallery = ({ images, disabled = false }) => {
  const { t } = useTranslation();
  // 以 src 為單位追蹤是否載完，避免 src 切換（例如 stream ready 後從 blur → thumbnail_url）時 loaded 殘留
  const [loadedSrcs, setLoadedSrcs] = useState(new Set());
  const [errorIds, setErrorIds] = useState(new Set());
  const addLoadedSrc = useCallback((src) => setLoadedSrcs(prev => new Set([...prev, src])), []);
  const addErrorId = useCallback((id) => setErrorIds(prev => new Set([...prev, id])), []);
  const lightboxRef = useRef(null);
  const galleryRef = useRef(null);
  const eventHandlerRef = useRef(null);
  const initHandlerRef = useRef(null);
  const closeHandlerRef = useRef(null);
  const beforeSlideHandlerRef = useRef(null);
  const afterSlideHandlerRef = useRef(null);
  const afterAppendSlideHandlerRef = useRef(null);
  const lgInitializedRef = useRef(false);
  const isLightboxOpenRef = useRef(false);
  const lgIndexMapRef = useRef({});
  // 記錄上次初始化時進入燈箱的媒體 ID 組合，用來判斷是否需要重新初始化
  const prevGalleryKeyRef = useRef(null);
  // 本實例專屬的 lightGallery class，用來把 DOM 查詢限定在這個燈箱
  // （燈箱 outer 掛在 body，feed 上多個 VideoGallery 會同時存在，不能用全域 querySelector）
  const instanceClassRef = useRef(`lg-vg-${Math.random().toString(36).slice(2, 10)}`);


  // 圖片佈局邏輯 (與 PostImages 保持一致)
  const getColClass = (index, imageCount) => {
    switch (imageCount) {
      case 1:
        return "col-12";
      case 2:
        return "col-6";
      case 3:
        return index < 2 ? "col-6" : "col-12";
      default:
        return "col-6";
    }
  };



  useEffect(() => {
    // 燈箱開啟中跳過，避免中斷使用者觀看
    if (isLightboxOpenRef.current) return;

    // 只比較真正會進入燈箱的媒體 ID（ready 的影片 + 有效圖片）
    // 避免按讚、留言等操作造成 images 參考改變但內容相同時不必要地重新初始化
    const galleryKey = (images ?? [])
      .filter(m =>
        m.type === 1
          ? (m.playback_url && m.stream_status === 'ready')
          : !!(m.big_url || m.url)
      )
      .map(m => `${m.id}:${m.caption || ''}`)
      .join(',');

    if (galleryKey === prevGalleryKeyRef.current && lightboxRef.current) {
      return;
    }
    prevGalleryKeyRef.current = galleryKey;

    // 清理舊實例（含 listener 與 lightGallery instance）
    if (galleryRef.current) {
      if (initHandlerRef.current) {
        galleryRef.current.removeEventListener('lgInit', initHandlerRef.current);
        initHandlerRef.current = null;
      }
      if (eventHandlerRef.current) {
        galleryRef.current.removeEventListener('lgAfterOpen', eventHandlerRef.current);
        eventHandlerRef.current = null;
      }
      if (closeHandlerRef.current) {
        galleryRef.current.removeEventListener('lgAfterClose', closeHandlerRef.current);
        closeHandlerRef.current = null;
      }
      if (beforeSlideHandlerRef.current) {
        galleryRef.current.removeEventListener('lgBeforeSlide', beforeSlideHandlerRef.current);
        beforeSlideHandlerRef.current = null;
      }
      if (afterSlideHandlerRef.current) {
        galleryRef.current.removeEventListener('lgAfterSlide', afterSlideHandlerRef.current);
        afterSlideHandlerRef.current = null;
      }
      if (afterAppendSlideHandlerRef.current) {
        galleryRef.current.removeEventListener('lgAfterAppendSlide', afterAppendSlideHandlerRef.current);
        afterAppendSlideHandlerRef.current = null;
      }
    }
    if (lightboxRef.current) {
      try { lightboxRef.current.destroy(true); } catch { /* 靜默處理銷毀錯誤 */ }
      lightboxRef.current = null;
    }
    lgInitializedRef.current = false;
    isLightboxOpenRef.current = false;

    if (!images?.length || !galleryRef.current) return;

    // 組裝燈箱 items
    const items = [];
    const indexMap = {};
    images.forEach((media, originalIndex) => {
      if (media.type === 1) {
        if (!media.playback_url || (media.stream_status && media.stream_status !== 'ready')) {
          return;
        }
        indexMap[originalIndex] = items.length;
        items.push({
          src: getStreamEmbedUrl(media.playback_url, {
            autoplay: true,
            muted: true,
            preload: 'auto',
            poster: media.thumbnail_url,
          }),
          iframe: true,
          thumb: media.thumbnail_url,
          subHtml: `<h4>${escapeHtml(media.caption || '')}</h4>`
        });
      } else {
        const imageSrc = media.big_url || media.url;
        if (!imageSrc) return;
        indexMap[originalIndex] = items.length;
        items.push({
          src: imageSrc,
          thumb: media.thumbnail_url || media.url || media.big_url,
          subHtml: `<h4>${escapeHtml(media.caption || '')}</h4>`
        });
      }
    });
    lgIndexMapRef.current = indexMap;

    if (items.length === 0) return;

    try {
      const eventHandler = () => { isLightboxOpenRef.current = true; };
      const closeHandler = () => { isLightboxOpenRef.current = false; };
      const initHandler = () => { lgInitializedRef.current = true; };
      const currentSlideSelector = `.${instanceClassRef.current} .lg-current iframe.lg-object`;
      // 即將離開的 slide：清空 iframe src，立即停止播放（不還原，避免背景持續下載）
      const beforeSlideHandler = () => {
        const currentIframe = document.querySelector(currentSlideSelector);
        if (currentIframe) currentIframe.src = 'about:blank';
      };
      // 進入新 slide：若 iframe 被 about:blank 清過（回到舊 slide），重設為 autoplay src
      const afterSlideHandler = (event) => {
        const index = event.detail?.index;
        if (typeof index !== 'number') return;
        const item = items[index];
        if (!item?.iframe) return;
        const iframe = document.querySelector(currentSlideSelector);
        if (!iframe) return;
        if (iframe.src !== item.src) iframe.src = item.src;
      };
      // lightGallery 會預載前後 slide（preload 預設 2），影片 iframe 帶 autoplay 一插入就會在背景靜音播放。
      // 非目前 slide 的影片插入後立即清空 src，滑到該張時由 afterSlideHandler 還原；圖片維持預載
      const afterAppendSlideHandler = (event) => {
        const { index } = event.detail;
        if (!items[index].iframe) return;
        if (index === lightboxRef.current.index) return;
        // 不在 DOM 的 slide（超出 numberOfSlideItemsInDom 視窗）也會觸發此事件，
        // 這時 lgQuery.find 會退回 document 查詢而誤抓其他 slide 的 iframe，必須先擋掉
        const slideEl = lightboxRef.current.getSlideItem(index).get();
        if (!slideEl) return;
        slideEl.querySelector('iframe.lg-object').src = 'about:blank';
      };

      eventHandlerRef.current = eventHandler;
      initHandlerRef.current = initHandler;
      closeHandlerRef.current = closeHandler;
      beforeSlideHandlerRef.current = beforeSlideHandler;
      afterSlideHandlerRef.current = afterSlideHandler;
      afterAppendSlideHandlerRef.current = afterAppendSlideHandler;

      galleryRef.current.addEventListener('lgInit', initHandler);
      galleryRef.current.addEventListener('lgAfterOpen', eventHandler);
      galleryRef.current.addEventListener('lgAfterClose', closeHandler);
      galleryRef.current.addEventListener('lgBeforeSlide', beforeSlideHandler);
      galleryRef.current.addEventListener('lgAfterSlide', afterSlideHandler);
      galleryRef.current.addEventListener('lgAfterAppendSlide', afterAppendSlideHandler);

      lightboxRef.current = lightGallery(galleryRef.current, {
        plugins: [lgThumbnail, lgFullscreen],
        licenseKey: 'GPLv3',
        addClass: instanceClassRef.current,
        dynamic: true,
        dynamicEl: items,
        speed: 500,
        download: false,
        counter: true,
        thumbnail: true,
        animateThumb: false,
        toggleThumb: true,
        allowMediaOverlap: false,
        zoomFromOrigin: false,
        hideScrollbar: true,
        responsive: true,
      });
    } catch (error) {
      console.error('VideoGallery lightGallery 初始化失敗:', error);
    }
  }, [images]);

  // 組件卸載時的清理
  useEffect(() => {
    const currentGalleryRef = galleryRef.current;
    const currentInitHandler = initHandlerRef.current;
    const currentEventHandler = eventHandlerRef.current;
    const currentCloseHandler = closeHandlerRef.current;

    return () => {
      // 清理事件監聽器
      if (currentGalleryRef) {
        if (currentInitHandler) {
          currentGalleryRef.removeEventListener('lgInit', currentInitHandler);
        }
        if (currentEventHandler) {
          currentGalleryRef.removeEventListener('lgAfterOpen', currentEventHandler);
        }
        if (currentCloseHandler) {
          currentGalleryRef.removeEventListener('lgAfterClose', currentCloseHandler);
        }
        if (beforeSlideHandlerRef.current) {
          currentGalleryRef.removeEventListener('lgBeforeSlide', beforeSlideHandlerRef.current);
        }
        if (afterSlideHandlerRef.current) {
          currentGalleryRef.removeEventListener('lgAfterSlide', afterSlideHandlerRef.current);
        }
        if (afterAppendSlideHandlerRef.current) {
          currentGalleryRef.removeEventListener('lgAfterAppendSlide', afterAppendSlideHandlerRef.current);
        }
      }

      // 銷毀實例
      if (lightboxRef.current) {
        try {
          lightboxRef.current.destroy(true);
        } catch (error) {
          // 靜默處理銷毀錯誤
        }
        lightboxRef.current = null;
      }

      // 重置狀態
      lgInitializedRef.current = false;
      isLightboxOpenRef.current = false;
    };
  }, []);

  const renderVideoOverlay = (media, isLoaded) => {
    const status = media.stream_status;
    if (status === 'error') {
      return (
        <div className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center bg-dark bg-opacity-75" style={{ pointerEvents: 'none' }}>
          <FontAwesomeIcon icon={['fas', 'circle-exclamation']} className="text-danger fs-3 mb-1" />
          <span className="text-white fs-12">{t('video_process_failed')}</span>
        </div>
      );
    }
    // 沒有 playback_url 代表尚未可播放（通常仍在處理中）
    if (!media.playback_url || (status && status !== 'ready')) {
      return (
        <div className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center bg-dark bg-opacity-75" style={{ pointerEvents: 'none' }}>
          <div className="spinner-border text-white mb-1" style={{ width: '1.5rem', height: '1.5rem' }} role="status" />
          <span className="text-white fs-12">{t('video_processing')}</span>
        </div>
      );
    }
    if (!isLoaded) {
      return (
        <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-dark bg-opacity-50" style={{ pointerEvents: 'none' }}>
          <div className="spinner-border text-white" style={{ width: '1.5rem', height: '1.5rem' }} role="status" />
        </div>
      );
    }
    return (
      <div className="position-absolute top-50 start-50 translate-middle" style={{ pointerEvents: 'none' }}>
        <div className="bg-dark bg-opacity-75 rounded-circle d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px' }}>
          <FontAwesomeIcon icon={['fas', 'play']} className="text-white fs-5" />
        </div>
      </div>
    );
  };

  const openGallery = (index = 0) => {
    if (disabled) return;

    // 非 ready 影片不在燈箱中，直接擋掉
    const lgIndex = lgIndexMapRef.current[index];
    if (lgIndex === undefined) return;

    if (lightboxRef.current && lgInitializedRef.current) {
      try {
        lightboxRef.current.openGallery(lgIndex);
      } catch (error) {
        console.error('VideoGallery: 開啟燈箱失敗:', error);
      }
    }
  };

  if (!images || images.length === 0) return null;

  // 最多顯示4張圖片/影片
  const displayLimit = 4;

  return (
    <div>
      <div className="row mt-2 mx-0 g-1" ref={galleryRef}>
        {images.map((media, index) => {
          if (index < displayLimit) {
            const isSingleImage = images.length === 1;
            const isErrored = errorIds.has(media.id);
            const isVideoNotReady =
              media.type === 1 &&
              (!media.playback_url || (media.stream_status && media.stream_status !== 'ready'));
            // 影片未 ready 不可點擊；破圖也不可點擊
            const isClickable = !disabled && !isErrored && !isVideoNotReady;
            const currentSrc = media.type === 1
              ? (isVideoNotReady ? thumbnail_video_blur : media.thumbnail_url)
              : media.url;
            const isCurrentSrcLoaded = loadedSrcs.has(currentSrc);
            return (
              <div
                key={media.id}
                className={`${getColClass(index, Math.min(images.length, 4))} ${
                  isSingleImage ? 'post-single-image' : 'post-multiple-images'
                }`}
              >
                <div className={`position-relative h-100 mb-2${isSingleImage ? ' w-100 text-center' : ''}`}>
                  {isSingleImage ? (
                    // 單張：破圖給明確高度；正常時 img 外包 position-relative 讓 overlay 定位相對 img
                    isErrored ? (
                      <BrokenMedia className="w-100" style={{ height: '300px' }} />
                    ) : (
                      <div className="position-relative d-inline-block">
                        <SafeImg
                          key={currentSrc}
                          src={currentSrc}
                          alt="Uploaded"
                          className={isClickable ? 'pointer' : ''}
                          onLoad={() => addLoadedSrc(currentSrc)}
                          onError={() => {
                            if (media.type === 1 && isVideoNotReady) return;
                            addErrorId(media.id);
                          }}
                          onClick={() => isClickable && openGallery(index)}
                        />
                        {media.type === 1 && renderVideoOverlay(media, isCurrentSrcLoaded)}
                      </div>
                    )
                  ) : (
                    // 多張：用 SafeImg 取代 SafeBg，img.onError 觸發更可靠
                    <div
                      className={`position-relative h-100 ${isClickable ? 'pointer' : ''}`}
                      onClick={() => isClickable && openGallery(index)}
                    >
                      {isErrored ? (
                        <BrokenMedia className="w-100 h-100" />
                      ) : (
                        <>
                          <SafeImg
                            key={currentSrc}
                            src={currentSrc}
                            onLoad={() => addLoadedSrc(currentSrc)}
                            onError={() => {
                              if (media.type === 1 && isVideoNotReady) return;
                              addErrorId(media.id);
                            }}
                            style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', display: 'block' }}
                          />
                          {media.type === 1 && renderVideoOverlay(media, isCurrentSrcLoaded)}
                        </>
                      )}
                    </div>
                  )}

                  {/* +N 遮罩 - 不論是圖片還是影片都顯示 */}
                  {images.length > 4 && index === 3 && (
                    <div
                      data-ignore="true"
                      className={`plus-img position-absolute text-light h-100 w-100 ${disabled ? '' : 'pointer'}`}
                      onClick={() => !disabled && openGallery(4)}
                    >
                      +{images.length - 4}
                    </div>
                  )}
                </div>
              </div>
            );
          }
          return null;
        })}
      </div>
    </div>
  );
};

VideoGallery.propTypes = {
  images: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      type: PropTypes.number.isRequired,
      url: PropTypes.string,
      big_url: PropTypes.string,
      thumbnail_url: PropTypes.string,
      playback_url: PropTypes.string, // CF Stream iframe URL (videos)
      stream_status: PropTypes.string, // CF Stream status (videos)
      caption: PropTypes.string
    })
  ),
  disabled: PropTypes.bool
};

export default VideoGallery;